import React, { useState, useCallback } from "react";
import { X, Upload, FileText, Trash2, Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import { LabService } from "../../../../../services/api/LaborderService/lab.service";

interface LabtestUploadReportSidebarProps {
  orderId: number;
  orderNumber: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const LabtestUploadReportSidebar: React.FC<LabtestUploadReportSidebarProps> = ({
  orderId,
  orderNumber,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const filesArray = Array.from(e.dataTransfer.files);
      setSelectedFiles((prev) => [...prev, ...filesArray]);
    }
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const filesArray = Array.from(e.target.files);
      setSelectedFiles((prev) => [...prev, ...filesArray]);
    }
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (selectedFiles.length === 0) {
      toast.error("Please select at least one report file to upload.");
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await LabService.updateLabOrder(orderId, selectedFiles);
      if (res.success) {
        toast.success("Lab report(s) uploaded successfully!");
        onSuccess();
        onClose();
      } else {
        toast.error(res.message || "Failed to upload report.");
      }
    } catch (error) {
      console.error("Error uploading reports:", error);
      toast.error("Failed to upload report documents.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200000] flex justify-end">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm cursor-pointer"
        onClick={onClose}
      />

      {/* Sidebar Panel */}
      <div className="relative w-full max-w-[450px] h-full bg-white shadow-2xl z-[200001] flex flex-col animate-slideLeft">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div>
            <h3 className="text-sm font-extrabold text-slate-800">Upload Lab Report</h3>
            <p className="text-[10px] font-bold text-slate-400 mt-0.5">ORDER #{orderNumber}</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-slate-50 text-slate-500 hover:bg-red-50 hover:text-red-500 flex items-center justify-center transition-colors border-none cursor-pointer"
          >
            <X size={16} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5 flex flex-col gap-5">
          {/* Drag & Drop Area */}
          <div
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-xl p-8 flex flex-col items-center justify-center text-center cursor-pointer transition-colors ${
              dragActive
                ? "border-emerald-500 bg-emerald-50/30"
                : "border-slate-200 hover:border-slate-300 bg-slate-50/50"
            }`}
            onClick={() => document.getElementById("report-file-input")?.click()}
          >
            <input
              type="file"
              id="report-file-input"
              className="hidden"
              multiple
              onChange={handleFileChange}
              accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
            />
            <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-600 mb-3">
              <Upload size={20} />
            </div>
            <p className="text-xs font-bold text-slate-700">Drag & drop your files here, or browse</p>
            <p className="text-[10px] text-slate-400 font-semibold mt-1">Supports PDF, PNG, JPG, JPEG, DOC</p>
          </div>

          {/* Selected Files List */}
          {selectedFiles.length > 0 && (
            <div className="flex-1 flex flex-col min-h-0">
              <h4 className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider mb-2">
                Selected Documents ({selectedFiles.length})
              </h4>
              <div className="flex-1 overflow-y-auto space-y-2 pr-1" style={{ scrollbarWidth: "thin" }}>
                {selectedFiles.map((file, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between p-3 bg-slate-50 border border-slate-100 rounded-xl"
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <FileText size={16} className="text-emerald-500 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-xs font-bold text-slate-700 truncate" title={file.name}>
                          {file.name}
                        </p>
                        <p className="text-[9px] text-slate-400 font-semibold">
                          {(file.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleRemoveFile(idx)}
                      className="p-1.5 text-slate-450 hover:text-red-500 rounded-lg hover:bg-red-50/50 transition-colors border-none bg-transparent cursor-pointer"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="p-5 border-t border-slate-100 flex items-center gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 rounded-xl text-xs font-bold bg-slate-100 text-slate-650 hover:bg-slate-200 transition-colors border-none cursor-pointer"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || selectedFiles.length === 0}
            className="flex-1 py-2.5 rounded-xl text-xs font-bold bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:shadow-lg transition-all border-none cursor-pointer disabled:opacity-40 flex items-center justify-center gap-1.5"
          >
            {isSubmitting ? (
              <>
                <Loader2 size={14} className="animate-spin" /> Uploading...
              </>
            ) : (
              "Submit Reports"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LabtestUploadReportSidebar;
